// react
import { useEffect } from "react";
//Formik
import { useFormik, Form, FormikProvider } from "formik";

// router
import {
  Link as RouterLink,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";

//MUI Components
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import { Icon } from "@iconify/react";

// custom components, constants and services
import { tagFormValidationSchema } from "src/util/validationSchemas";
import { BaseTable } from "../BaseTable/BaseTable";

//custom hooks
import { useAccess } from "src/hooks/useAccess.hook";
import { useUser } from "src/hooks/useUser.hook";
import { useMutate, useGetDetail } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { getTeamUrl, tagIdUrl, tagUrl } from "src/react-query/endPoints";
import { is } from "src/util/is";
import { getTagDefaultValues, tagFormChargersColumns, tagUsersColumn } from "./Tags.constants";
import { Button, Card } from "@mui/material";
import { FEATURE } from "src/util/enums";
import { Loader } from "../Loader";
import { f } from "src/utils/formatTime";
import { useSelector } from "react-redux";

function TagForm() {
  const navigate = useNavigate();
  const { teamId } = useSelectedTeam();
  const isCreateTag = useMatch("/tag/new");
  const { data: userData } = useUser();
  const { tagId } = useParams();
  const hasReadOnlyAccess = useAccess(FEATURE.TAG, "R");

  const isReadOnly = hasReadOnlyAccess && !isCreateTag;

  const { data, isFetching } = useGetDetail("tag", { id: tagId });
  const { data: tagUsers, isFetching: isTagUsersFetching } = useGetDetail(
    "tagsUser",
    { id: tagId }
  );
  const {
    data: tagsChargerList,
    isLoading: isTagsChargerFetching,
    refetch,
  } = useGetDetail("tagsCharger", { id: tagId });

  const { tabOpened, tabOpenedFor,detailsId } = useSelector(
    ({ tabsCurrentlyOpenedFor }) => tabsCurrentlyOpenedFor
  );
  const { formData,formDataOf} = useSelector(
    ({ formData }) => formData
  );
  const { mutate, isLoading } = useMutate("tags");
  const onSubmit = async (tagData, { setSubmitting }) => {
    const mutateObject = {
      url: getTeamUrl(teamId, tagUrl),
      method: "POST",
    };
    if (!is.nullOrUndefined(tagId) && !isCreateTag) {
      mutateObject.method = "PUT";
      mutateObject.url = getTeamUrl(teamId, tagIdUrl(tagId));
    }
    tagData.companyId = teamId;
    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: tagData,
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const onSuccess = () => {
    if(formDataOf==="customer" && !is.empty(formData) && tabOpenedFor==="customer" && tabOpened==="tags"){
        !is.empty(detailsId)? navigate(`/customer/${detailsId}`): navigate(`/customer/new`);
        return;
    }  
    navigate("/tag")
  };

  const formik = useFormik({
    initialValues: getTagDefaultValues(teamId, userData?.data?.id),
    validationSchema: tagFormValidationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
    setFieldValue,
    setFieldTouched,
  } = formik;


  useEffect(() => {
    if (data) {
      const [tag] = data;
      
      // Ensure all fields have proper default values to prevent controlled/uncontrolled warnings
      const sanitizedTag = {
        tag: tag.tag || "",
        emailId: tag.emailId || "",
        parenttag: tag.parenttag || "",
        expirydate: tag.expirydate ? f(tag.expirydate, "yyyy-MM-dd") : "",
        max_active_trans_count: tag.max_active_trans_count || 0,
        note: tag.note || "",
        chargerpointId: tag.chargerpointId || 0,
        companyId: tag.companyId || teamId,
        userId: tag.userId || userData?.data?.id,
        balance_amount: tag.balance_amount || "",
        blocked: tag.blocked === "1" || tag.blocked === 1 || tag.blocked === true,
        in_transaction: tag.in_transaction === "1" || tag.in_transaction === 1 || tag.in_transaction === true
      };
      
      resetForm({ values: sanitizedTag });
    }
  }, [data, resetForm, teamId, userData?.data?.id]);

  if (isFetching) {
    return <Loader />;
  }

  if (!isCreateTag && is.empty(data)) {
    navigate("/tag");
    return null;
  }

  return (
    <Box sx={{ 
      maxWidth: 1000, 
      mx: 'auto', 
      p: 2,
      background: (theme) => theme.palette.background.default
    }}>
      <FormikProvider value={formik}>
        <Form
          autoComplete="off"
          id="authcodes"
          noValidate
          onSubmit={handleSubmit}
        >
          {/* Header Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 600, 
              color: (theme) => theme.palette.primary.main,
              mb: 0.5
            }}>
              {isCreateTag ? 'Create New Tag' : 'Tag Details'}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: (theme) => theme.palette.text.secondary
            }}>
              {isCreateTag 
                ? 'Configure and create a new tag for your charging network'
                : 'View and manage tag information and settings'
              }
            </Typography>
          </Box>

          {/* Main Form Content */}
          <Paper sx={{ 
            p: 2, 
            borderRadius: 1,
            boxShadow: (theme) => theme.customShadows.card,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            background: (theme) => theme.palette.background.paper
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              pb: 1,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`
            }}>
              <Icon icon="eva:credit-card-outline" width={20} height={20} style={{ 
                color: (theme) => theme.palette.primary.main,
                marginRight: '8px'
              }} />
              <Typography variant="h6" sx={{ 
                fontWeight: 500, 
                color: (theme) => theme.palette.primary.main
              }}>
                Tag Configuration
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  required
                  label="RFID Tag ID"
                  size="small"
                  disabled={isReadOnly}
                  {...getFieldProps("tag")}
                  error={Boolean(touched.tag && errors.tag)}
                  helperText={touched.tag && errors.tag}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Balance Amount"
                  size="small"
                  disabled={!isCreateTag}
                  {...getFieldProps("balance_amount")}
                  error={Boolean(
                    isCreateTag &&
                      touched.balance_amount &&
                      errors.balance_amount
                  )}
                  helperText={
                    isCreateTag &&
                    touched.balance_amount &&
                    errors.balance_amount
                  }
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mr: 1,
                        color: 'text.secondary',
                        fontWeight: 500
                      }}>
                        ₹
                      </Box>
                    )
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Parent Group</InputLabel>
                  <Select
                    disabled={isReadOnly}
                    label="Parent Group"
                    {...getFieldProps("parenttag")}
                  >
                    <MenuItem value="">None</MenuItem>
                    {!isFetching &&
                      data &&
                      data.map((tag) => (
                        <MenuItem key={tag.parenttag} value={tag.parenttag}>
                          {tag.parenttag}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  {...getFieldProps("max_active_trans_count")}
                  size="small"
                  disabled={isReadOnly}
                  label="Max Active Transactions"
                  helperText="0=block, negative=unlimited, positive=limit"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Expiry Date"
                  type="date"
                  size="small"
                  disabled={isReadOnly}
                  {...getFieldProps("expirydate")}
                  error={Boolean(touched.expirydate && errors.expirydate)}
                  helperText={touched.expirydate && errors.expirydate}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: new Date().toISOString().split('T')[0] // Restrict to today and future dates
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  size="small"
                  disabled={isReadOnly}
                  multiline
                  rows={2}
                  {...getFieldProps("note")}
                  placeholder="Add any additional notes or comments about this RFID tag..."
                  fullWidth
                />
              </Grid>
            </Grid>
          </Paper>
          {!isCreateTag && (
            <Paper sx={{ 
              p: 2, 
              mt: 2, 
              borderRadius: 1,
              boxShadow: (theme) => theme.customShadows.card,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              background: (theme) => theme.palette.background.paper
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                pb: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`
              }}>
                <Icon icon="eva:person-outline" width={20} height={20} style={{ 
                  color: (theme) => theme.palette.primary.main,
                  marginRight: '8px'
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 500, 
                  color: (theme) => theme.palette.primary.main
                }}>
                  Associated EV Driver
                </Typography>
              </Box>

              {!is.empty(tagUsers) ? (
                <Grid container spacing={2}>
                  {tagUsers.map(({ userPk, email, firstName, lastName }, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`${index}-${firstName}-${index}`}>
                      <Box sx={{
                        p: 2,
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        background: (theme) => theme.palette.background.paper
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar sx={{ 
                            background: (theme) => theme.palette.primary.main,
                            mr: 1.5,
                            width: 32,
                            height: 32
                          }}>
                            <Icon icon="eva:person-fill" width={16} height={16} />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ 
                              fontWeight: 600, 
                              fontSize: '0.9rem',
                              color: 'text.primary',
                              mb: 0.25
                            }}>
                              {firstName} {lastName}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: 'text.secondary',
                              fontSize: '0.75rem'
                            }}>
                              ID: {userPk}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ 
                          color: 'text.primary',
                          fontSize: '0.8rem'
                        }}>
                          {email}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 3,
                  px: 2,
                  borderRadius: 1,
                  border: (theme) => `1px dashed ${theme.palette.divider}`,
                  background: (theme) => theme.palette.action.hover
                }}>
                  <Icon icon="eva:person-outline" width={32} height={32} style={{ 
                    color: 'text.secondary', 
                    marginBottom: '8px' 
                  }} />
                  <Typography variant="body1" sx={{ 
                    color: 'text.primary', 
                    mb: 0.5,
                    fontWeight: 500
                  }}>
                    No EV Driver Associated
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    fontSize: '0.8rem'
                  }}>
                    This RFID tag is not currently associated with any EV driver
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
          {!isCreateTag && (
            <Paper sx={{ 
              p: 2, 
              mt: 2, 
              borderRadius: 1,
              boxShadow: (theme) => theme.customShadows.card,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              background: (theme) => theme.palette.background.paper
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                pb: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`
              }}>
                <Icon icon="eva:charging-outline" width={20} height={20} style={{ 
                  color: (theme) => theme.palette.primary.main,
                  marginRight: '8px'
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 500, 
                  color: (theme) => theme.palette.primary.main
                }}>
                  Attached Chargers
                </Typography>
              </Box>

              {tagsChargerList && tagsChargerList.length > 0 ? (
                <Box sx={{ 
                  borderRadius: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  minHeight: 'auto !important',
                  '& .MuiStack-root': {
                    minHeight: 'auto !important',
                    '& .MuiBox-root': {
                      minHeight: 'auto !important',
                      height: 'auto !important',
                      '& .MuiDataGrid-root': {
                        border: 'none !important',
                        minHeight: 'auto !important',
                        height: 'auto !important',
                        '& .MuiDataGrid-main': {
                          minHeight: 'auto !important',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                          minHeight: 'auto !important',
                        },
                        '& .MuiDataGrid-cell': {
                          padding: '6px 8px !important',
                          fontSize: '0.75rem',
                          minHeight: '28px !important',
                          maxHeight: '28px !important',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          padding: '0 8px',
                          minHeight: '36px !important',
                        },
                        '& .MuiDataGrid-columnHeader': {
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          minHeight: '36px !important',
                        },
                        '& .MuiDataGrid-row': {
                          minHeight: '28px !important',
                          maxHeight: '28px !important',
                        },
                        '& .MuiDataGrid-footerContainer': {
                          minHeight: '32px !important',
                          maxHeight: '32px !important',
                        }
                      }
                    }
                  }
                }}>
                  <BaseTable
                    height={100}
                    rows={tagsChargerList ?? []}
                    columns={tagFormChargersColumns()}
                    getRowId={(row) => row.charge_box_id}
                    loading={isTagsChargerFetching}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 2,
                  px: 2,
                  borderRadius: 1,
                  border: (theme) => `1px dashed ${theme.palette.divider}`,
                  background: (theme) => theme.palette.action.hover
                }}>
                  <Icon icon="eva:charging-outline" width={24} height={24} style={{ 
                    color: 'text.secondary', 
                    marginBottom: '8px' 
                  }} />
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    fontSize: '0.8rem'
                  }}>
                    No Chargers Attached
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
          {/* Action Buttons */}
          <Box sx={{ 
            mt: 2, 
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <Stack direction="row" spacing={1}>
              {isReadOnly ? (
                <Button
                  size="small"
                  type="button"
                  variant="contained"
                  component={RouterLink}
                  to="/tag"
                  startIcon={<Icon icon="eva:arrow-back-outline" />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Back
                </Button>
              ) : (
                <>
                  <Button
                    size="small"
                    type="button"
                    variant="outlined"
                    component={RouterLink}
                    to="/tag"
                    startIcon={<Icon icon="eva:close-outline" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    size="small"
                    type="submit"
                    variant="contained"
                    disabled={isReadOnly}
                    loading={isSubmitting || isLoading}
                    startIcon={<Icon icon="eva:save-outline" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    {isSubmitting || isLoading ? 'Saving...' : 'Save Changes'}
                  </LoadingButton>
                </>
              )}
            </Stack>
          </Box>
        </Form>
      </FormikProvider>
    </Box>
  );
}

export default TagForm;
